// Leading-dot suffixes match any subdomain (partner/mortgages/consumer.*) but
// never the bare apex, which hosts CMS/partner open-redirect and XSS sinks.
// Deliberately WIDER than Rails SafeRedirectPath::EXTERNAL_HOST_RULES, which
// allowlists `.simplersell.com`, `consumer.opendoor.com`, and `www.opendoor.com`
// by name: this accepts any `*.opendoor.com`, so a value Rails re-validates can
// still be dropped server-side, while the `window.location.assign` path in
// AccountFormV2 has no second check. First-party either way.
const TRUSTED_HOST_SUFFIXES = ['.opendoor.com', '.simplersell.com'];
const LOOPBACK_HOSTS = ['localhost', '127.0.0.1', '0.0.0.0'];
// A trusted suffix needs a real label in front of it: `.opendoor.com` and
// `..opendoor.com` both end with a suffix, and `new URL()` keeps either as a
// hostname, but neither is a legal DNS name. A length check isn't enough — it
// admits the double dot — so require well-formed labels throughout.
const HOSTNAME_LABELS = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/;

function isTrustedFirstPartyHost(host: string): boolean {
  const normalized = host.toLowerCase();
  if (LOOPBACK_HOSTS.includes(normalized)) return true;
  if (!HOSTNAME_LABELS.test(normalized)) return false;
  return TRUSTED_HOST_SUFFIXES.some((suffix) => normalized.endsWith(suffix));
}

/**
 * Returns a value safe to hand Rails as `redirect_to`, or undefined when the
 * target is not trusted. Accepts relative paths, same-origin absolute URLs
 * (reduced to `path + search + hash`), and trusted first-party absolute URLs
 * (the email/SMS deep-link shape `/login?redirectTo=https://consumer.opendoor.com/…`).
 */
// A single leading slash is not enough to keep a path same-origin: the URL parser
// folds `\` to `/` for http(s) and strips raw tab/CR/LF before it reads the
// authority, so `/\evil.com` and `/<TAB>/evil.com` both resolve to `//evil.com`.
//
// Only the leading position can smuggle an authority — the `^\/(?!\/)` test
// below already fixes index 0 as `/`, so index 1 is the only place one of these
// characters can turn into the second slash. This matches the whole string
// anyway, on purpose: Rails `SafeRedirectPath::UNSAFE_CHARS` rejects every C0
// control anywhere in the value, so a path carrying one is not a legitimate
// target for this system, and narrowing to the prefix would return a
// CRLF-bearing string to callers that may log or forward it. Every `redirectTo`
// our consumers build is a code-constructed path, so nothing legitimate is lost.
const AUTHORITY_SMUGGLING = /[\\\t\n\r]/;

export function sanitizeRedirectPath(path: string | undefined | null): string | undefined {
  if (!path) return undefined;
  if (/^\/(?!\/)/.test(path) && !AUTHORITY_SMUGGLING.test(path)) return path;

  const parsed = tryParseHttpUrl(path);
  if (parsed) {
    if (
      typeof window !== 'undefined' &&
      parsed.host.toLowerCase() === window.location.host.toLowerCase()
    ) {
      return parsed.pathname + parsed.search + parsed.hash;
    }
    // `hostname`, not `host`: the suffix and loopback lists hold bare hostnames,
    // so a ported target like `localhost:4000` would never match.
    if (isTrustedFirstPartyHost(parsed.hostname)) {
      return path;
    }
  }

  console.error(`[auth-fe] rejected non-relative redirect path: ${path}`);
  return undefined;
}

function tryParseHttpUrl(value: string): URL | undefined {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return undefined;
  }
  return url.protocol === 'https:' || url.protocol === 'http:' ? url : undefined;
}
